import { z } from 'zod';

const COUNTRY_OR_WILDCARD_RE = /^([A-Z]{2}|\*)$/;

// Shipping zone create payload: a name and the country list it covers ('*' = rest of world).
export const CreateShippingZoneSchema = z
  .object({
    name: z.string().min(2).max(255),
    countries: z
      .array(z.string().regex(COUNTRY_OR_WILDCARD_RE, "Countries must be 2-letter uppercase ISO codes or '*'"))
      .min(1)
      .max(100)
  })
  .strict();

// Shipping zone update payload: same fields, all optional, at least one required.
export const UpdateShippingZoneSchema = CreateShippingZoneSchema.partial().refine(
  (d) => Object.keys(d).length > 0,
  { message: 'At least one field must be provided' }
);

// Optional bound pairs shared by the rate create and update payloads.
interface RateBounds {
  minOrderPrice?: number;
  maxOrderPrice?: number;
  minWeightKg?: number;
  maxWeightKg?: number;
  estimatedMinDays?: number;
  estimatedMaxDays?: number;
}

// Reports whether every min/max pair present in the payload is correctly ordered.
const rateBoundsValid = (d: RateBounds): boolean =>
  (d.minOrderPrice === undefined || d.maxOrderPrice === undefined || d.minOrderPrice <= d.maxOrderPrice) &&
  (d.minWeightKg === undefined || d.maxWeightKg === undefined || d.minWeightKg <= d.maxWeightKg) &&
  (d.estimatedMinDays === undefined || d.estimatedMaxDays === undefined || d.estimatedMinDays <= d.estimatedMaxDays);

// Base rate object shared by create (required core fields) and update (all partial).
const shippingRateObject = z
  .object({
    name: z.string().min(2).max(255),
    rateType: z.enum(['FLAT', 'PRICE_BASED', 'WEIGHT_BASED']),
    price: z.number().min(0),
    minOrderPrice: z.number().min(0).optional(),
    maxOrderPrice: z.number().min(0).optional(),
    minWeightKg: z.number().min(0).optional(),
    maxWeightKg: z.number().min(0).optional(),
    estimatedMinDays: z.number().int().min(0).max(60).optional(),
    estimatedMaxDays: z.number().int().min(0).max(60).optional(),
    isActive: z.boolean().optional()
  })
  .strict();

// Shipping rate create payload with ordered min/max bounds when both are given.
export const CreateShippingRateSchema = shippingRateObject.refine(rateBoundsValid, {
  message: 'min bounds must not exceed their max bounds'
});

// Shipping rate update payload: all fields optional, at least one, bounds ordered when paired.
export const UpdateShippingRateSchema = shippingRateObject
  .partial()
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' })
  .refine(rateBoundsValid, { message: 'min bounds must not exceed their max bounds' });

// What the storefront asks for so it can show the shipping fee the order will actually be charged.
export const ShippingQuoteSchema = z
  .object({
    countryCode: z.string().regex(/^[A-Za-z]{2}$/, 'Country must be a 2-letter ISO code').optional(),
    subtotalPrice: z.number().nonnegative('Subtotal cannot be negative').max(10_000_000),
    weightKg: z.number().nonnegative().max(10_000).optional(),
    preferredRateId: z.string().uuid().optional()
  })
  .strict();

export type ShippingQuoteDto = z.infer<typeof ShippingQuoteSchema>;

export type CreateShippingZoneDto = z.infer<typeof CreateShippingZoneSchema>;
export type UpdateShippingZoneDto = z.infer<typeof UpdateShippingZoneSchema>;
export type CreateShippingRateDto = z.infer<typeof CreateShippingRateSchema>;
export type UpdateShippingRateDto = z.infer<typeof UpdateShippingRateSchema>;

/** Which country a storefront asks the free-delivery amount for; Morocco unless it says. */
export const FreeShippingQuerySchema = z.object({
  country: z.string().regex(/^[A-Za-z]{2}$/, 'Country must be a 2-letter ISO code').default('MA').transform((code) => code.toUpperCase())
});

/** The order total from which delivery is free, as the store's own rates price it, or null when none ever is. */
export const FreeShippingSchema = z.object({ threshold: z.number().nonnegative().nullable() });

export type FreeShippingQuery = z.infer<typeof FreeShippingQuerySchema>;
export type FreeShippingDto = z.infer<typeof FreeShippingSchema>;
